import { IllustrationPanel } from '@/components/step-flow/IllustrationPanel';
import { LangToggle } from '@/components/step-flow/LangToggle';
import { StepFlowFooter } from '@/components/step-flow/StepFlowFooter';
import {
  eyebrowClassName,
  logoClassName,
  shellColumnClassName,
  shellMainClassName,
  shellRootClassName,
} from '@/components/step-flow/StepFlowShell.styles';
import type { StepFlowShellProps } from '@/components/step-flow/StepFlowShell.types';
import { Box, Image, StepProgress, Text } from '@careflux/primitives';

const PROGRESS_LABEL = { is: 'Framvinda', en: 'Progress' } as const;

/** Generic full-viewport multi-step layout: form column on the left, illustration panel on the right. */
export const StepFlowShell = ({
  step,
  totalSteps,
  lang,
  onLangChange,
  stepLabel,
  title,
  subtitle,
  children,
  illustration,
  onBack,
  backLabel,
  onNext,
  nextLabel,
  nextDisabled,
  nextLoading,
}: StepFlowShellProps) => (
  <Box display="flex" width="full" overflow="hidden" background="card" className={shellRootClassName}>
    <Box
      display="flex"
      direction="col"
      width="full"
      minWidth="0"
      paddingX={{ base: 'md', sm: 'xl', lg: '2xl' }}
      paddingY="xl"
      className={shellColumnClassName}
    >
      <Box as="header" display="flex" align="center" justify="between">
        <Box display="flex" align="center" gap="sm">
          <Image src="/careflux-small.svg" alt="Careflux" objectFit="contain" className={logoClassName} />
          <Text weight="semibold">Skríban</Text>
        </Box>
        <LangToggle lang={lang} onLangChange={onLangChange} />
      </Box>

      <Box marginTop="2xl">
        <StepProgress total={totalSteps} current={step} valueText={stepLabel} aria-label={PROGRESS_LABEL[lang]} />
      </Box>
      <Box marginTop="sm">
        <Text asChild size="xs" weight="semibold" color="primary" transform="uppercase" className={eyebrowClassName}>
          <p>{stepLabel}</p>
        </Text>
      </Box>

      <Box as="main" key={step} marginTop="sm" paddingBottom="lg" overflow="y-auto" className={shellMainClassName}>
        <Text asChild variant="heading" size="3xl" weight="bold">
          <h1>{title}</h1>
        </Text>
        {subtitle && (
          <Box marginTop="sm">
            <Text asChild color="muted">
              <p>{subtitle}</p>
            </Text>
          </Box>
        )}
        <Box marginTop="xl">{children}</Box>
      </Box>

      <StepFlowFooter
        onBack={onBack}
        backLabel={backLabel}
        onNext={onNext}
        nextLabel={nextLabel}
        nextDisabled={nextDisabled}
        nextLoading={nextLoading}
      />
    </Box>

    <IllustrationPanel>{illustration}</IllustrationPanel>
  </Box>
);
